import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { SupplierService, Supplier } from '../../../../core/services/supplier.service';
import { PaginationService } from '../../../../core/services/pagination.service';
import { extractApiError } from '../../../../core/utils/api-format';
import { getRussianPaginatorIntl } from '../../../../core/providers/russian-paginator-intl';
import { QuotaHeaderComponent } from '../../components/quota-header/quota-header.component';

@Component({
  selector: 'app-reason-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <mat-form-field appearance="outline" style="width:100%; margin-top:8px">
        <mat-label>Причина</mat-label>
        <textarea matInput [(ngModel)]="reason" rows="3" cdkFocusInitial required></textarea>
        <mat-error>Причина обязательна</mat-error>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close(null)">Отмена</button>
      <button mat-raised-button color="primary" [disabled]="!reason.trim()" (click)="dialogRef.close(reason.trim())">Подтвердить</button>
    </mat-dialog-actions>
  `
})
export class ReasonDialogComponent {
  dialogRef = inject(MatDialogRef<ReasonDialogComponent>);
  data: { title: string } = inject(MAT_DIALOG_DATA);
  reason = '';
}

@Component({
  selector: 'app-my-companies',
  standalone: true,
  imports: [CommonModule, QuotaHeaderComponent, MatIconModule, MatButtonModule, MatSnackBarModule, MatDialogModule, MatTooltipModule, MatPaginatorModule],
  providers: [{ provide: MatPaginatorIntl, useValue: getRussianPaginatorIntl() }],
  templateUrl: './my-companies.component.html',
  styleUrls: ['./my-companies.component.css']
})
export class MyCompaniesComponent implements OnInit {
  private supplierService = inject(SupplierService);
  private paginationService = inject(PaginationService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  companies: Supplier[] = [];
  loading = true;
  error: string | null = null;
  totalElements = 0;
  actionLoading: Record<string, boolean> = {};
  pagination = this.paginationService.createPaginationState(15);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;

    const { page, size } = this.pagination.getParams();
    this.supplierService.getMySuppliers(page, size).subscribe({
      next: (response) => {
        this.companies = response.res.content;
        this.totalElements = response.res.totalElements;
        this.pagination.setTotalElements(response.res.totalElements);
        this.loading = false;
      },
      error: (err) => {
        this.error = extractApiError(err?.error, 'Не удалось загрузить компании');
        this.loading = false;
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pagination.onPageChange(event);
    this.load();
  }

  addCompany(): void {
    this.router.navigate(['/supplier-registration']);
  }

  applicantTypeLabel(type?: string): string {
    if (type === 'LIMITED_LIABILITY_COMPANY') return 'ОсОО';
    if (type === 'INDIVIDUAL_ENTREPRENEUR') return 'ИП';
    return type ?? '—';
  }

  supplierTypeLabel(type?: string): string {
    if (type === 'NEW_SUPPLIER') return 'Новый поставщик';
    if (type === 'EXISTING_SUPPLIER') return 'Действующий поставщик';
    return type ?? '—';
  }

  actingAsLabel(val?: string | null): string {
    if (val === 'DIRECTOR') return 'Директор';
    if (val === 'AGENT') return 'Агент';
    return val ?? '—';
  }

  deleteCompany(company: Supplier): void {
    const ref = this.dialog.open(ReasonDialogComponent, {
      width: '420px',
      data: { title: `Удалить компанию "${company.companyName}"?` }
    });
    ref.afterClosed().subscribe((reason: string | null) => {
      if (reason === null) return;
      this.actionLoading[company.id] = true;
      this.supplierService.updateSupplierAction(company.id, 'DELETE', reason).subscribe({
        next: () => {
          company.deleted = true;
          this.actionLoading[company.id] = false;
          this.snackBar.open('Компания удалена', 'OK', { duration: 3000, verticalPosition: 'top' });
        },
        error: (err) => {
          this.actionLoading[company.id] = false;
          const msg = err?.error?.msg || 'Ошибка при удалении';
          this.snackBar.open(msg, 'OK', { duration: 4000, verticalPosition: 'top' });
        }
      });
    });
  }

  restoreCompany(company: Supplier): void {
    const ref = this.dialog.open(ReasonDialogComponent, {
      width: '420px',
      data: { title: `Восстановить компанию "${company.companyName}"?` }
    });
    ref.afterClosed().subscribe((reason: string | null) => {
      if (reason === null) return;
      this.actionLoading[company.id] = true;
      this.supplierService.updateSupplierAction(company.id, 'RESTORE', reason).subscribe({
        next: () => {
          company.deleted = false;
          this.actionLoading[company.id] = false;
          this.snackBar.open('Компания восстановлена', 'OK', { duration: 3000, verticalPosition: 'top' });
        },
        error: (err) => {
          this.actionLoading[company.id] = false;
          const msg = err?.error?.msg || 'Ошибка при восстановлении';
          this.snackBar.open(msg, 'OK', { duration: 4000, verticalPosition: 'top' });
        }
      });
    });
  }
}
